"use client";

import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ResultBanner,
  PrintButton,
  CopyButton,
  DataReviewedNote,
  PrintFooter,
  trackToolResult,
  type Verdict,
} from "@/components/tools/ui";

/* Excavation support selector — a planning DECISION AID, not a temporary works
   design. Anything beyond the simplest, shallowest, firm-ground case is pushed
   to a temporary works engineer / competent person design.

   Verified against quotable sources via the rams-knowledge bank on 2026-06-06:
   - HSE "Excavations" (hse.gov.uk/construction/safetytopics/excavations.htm):
     "No ground can be relied upon to stand unsupported in all circumstances";
     "Depending on conditions, a cubic metre of soil can weigh in excess of
     1.5 tonnes"; decide temporary support BEFORE digging and have it on site
     before work starts; batter to a safe angle (flatter in wet/granular
     ground); keep plant and spoil back from the edge; barriers to stop falls.
   - CDM 2015 reg 22 (legislation.gov.uk/uksi/2015/51): all practicable steps
     to prevent collapse incl. supports or battering; no construction work in a
     supported/battered excavation until a competent person has inspected it at
     the start of the shift, after any event likely to affect stability, and
     after any fall of material.
   - HSG47 (avoiding danger from underground services): locate services
     (plans + CAT/Genny) before breaking ground.

   This tool never calls any excavation "safe", never gives a batter angle, and
   escalates >4 m / water / surcharge / unknown soil to engineer design. */

const HSE_EXCAVATIONS = "https://www.hse.gov.uk/construction/safetytopics/excavations.htm";

/* ── Inputs ──────────────────────────────────────────────────────────────── */

type Depth = "shallow" | "mid" | "deep";
type Soil = "firm-clay" | "sand-gravel" | "made-ground" | "waterlogged" | "mixed-unsure";
type YN = "yes" | "no" | "unsure";

const DEPTH_LABEL: Record<Depth, string> = {
  shallow: "Under 1.2 m",
  mid: "1.2 m to 4 m",
  deep: "Over 4 m, or not sure",
};

const SOIL_LABEL: Record<Soil, string> = {
  "firm-clay": "Firm, stable clay",
  "sand-gravel": "Sand or gravel (granular, loose)",
  "made-ground": "Made ground / fill / previously dug",
  waterlogged: "Waterlogged or running ground",
  "mixed-unsure": "Mixed, layered or not sure",
};

interface Answers {
  depth?: Depth;
  soil?: Soil;
  water?: YN;
  surcharge?: YN; // spoil, plant, traffic, structures within ~ the depth of the dig
  services?: YN; // services located with plans + CAT/Genny
  entry?: YN; // will anyone enter the excavation
  duration?: "short" | "extended"; // open for a single shift vs left open / re-entered
}

type Method =
  | "engineer" // temporary works engineer / competent-person design
  | "batter" // batter back to a safe angle (only the simplest case)
  | "support" // proprietary support — trench box / drag box / sheeting + props
  | "no-entry"; // keep people out; work from outside the excavation

interface Outcome {
  method: Method;
  level: Verdict;
  headline: string;
  reasoning: string;
  /** Why this isn't a DIY answer, when escalated. */
  escalation?: string;
}

/* ── Decision logic — conservative by construction ───────────────────────────
   Hard escalation triggers (engineer / competent-person DESIGN, no DIY answer):
     - depth over 4 m or unknown
     - waterlogged / running ground
     - significant surcharge nearby
     - soil unknown / mixed
   Only the simplest firm-ground shallow case with no aggravating factor gets a
   battering OR support steer; everything else with entry gets supported entry,
   and where entry can be avoided we say keep people out. */

function decide(a: Answers): Outcome {
  const depth = a.depth!;
  const soil = a.soil!;
  const water = a.water === "yes";
  const surcharge = a.surcharge === "yes";
  const entry = a.entry !== "no"; // treat unsure as "someone may enter"

  // ── Hard guardrails → engineer / competent-person design ──────────────
  if (depth === "deep") {
    return {
      method: "engineer",
      level: "veryhigh",
      headline: "This needs a temporary works engineer",
      reasoning:
        "An excavation over about 4 m deep (or where the depth isn't yet known) is beyond a pick-from-a-list decision. The support system has to be designed for the actual ground, depth and loads.",
      escalation:
        "Get a temporary works design from a competent person — typically a temporary works engineer working to a temporary works coordinator under BS 5975 — before anyone digs to depth or enters. Don't improvise the support on site.",
    };
  }
  if (soil === "waterlogged" || water) {
    return {
      method: "engineer",
      level: "veryhigh",
      headline: "This needs a temporary works engineer — water is present",
      reasoning:
        "Water makes ground far less predictable: it raises the pressure on the sides, washes out fines and can cause running ground and sudden collapse. HSE notes a much flatter slope is needed in wet ground, and the side loads on any support change.",
      escalation:
        "Get a temporary works design (and a dewatering / groundwater plan) from a competent person before excavating or entering. Battering and standard trench boxes can't be assumed to be adequate in water-bearing ground.",
    };
  }
  if (surcharge) {
    return {
      method: "engineer",
      level: "veryhigh",
      headline: "This needs a temporary works engineer — surcharge nearby",
      reasoning:
        "Spoil heaps, plant, traffic or building/wall foundations within roughly the depth of the dig add load (surcharge) to the sides and sharply increase the collapse risk. CDM 2015 reg 22(3) requires steps to stop the excavation and adjacent ground being overloaded.",
      escalation:
        "Get a temporary works design that accounts for the surcharge, and structural-engineer advice where you could undermine a wall or foundation. Move spoil and plant well back from the edge before any work proceeds.",
    };
  }
  if (soil === "mixed-unsure") {
    return {
      method: "engineer",
      level: "veryhigh",
      headline: "This needs a temporary works engineer — ground not known",
      reasoning:
        "If the ground is mixed, layered or unknown you can't reason about how it will behave. HSE is explicit that no ground can be relied upon to stand unsupported in all circumstances, so an unknown profile must be treated as needing designed support.",
      escalation:
        "Establish the ground conditions (trial holes, a ground investigation or local knowledge) and get a competent person to design the support before breaking ground.",
    };
  }

  // ── Made ground / fill (known, ≤4 m, dry, no surcharge) ───────────────
  // Disturbed/back-filled ground has no reliable structure — push to support
  // (designed where entry is involved), never battering.
  if (soil === "made-ground") {
    if (!entry) {
      return {
        method: "no-entry",
        level: "high",
        headline: "Keep people out — don't enter made ground unsupported",
        reasoning:
          "Made ground and previously-dug fill have no reliable structure and can collapse without warning. If nobody needs to be in the trench, the safest answer is to keep people out and work from outside it.",
      };
    }
    return {
      method: "support",
      level: "high",
      headline: "Proprietary support before any entry — and confirm it suits the fill",
      reasoning:
        "Entry into made ground or fill means installed support (a trench box / drag box, or sheeting and props) sized for the depth, never battering alone. Because fill is unpredictable, have a competent person confirm the chosen system is suitable for the actual ground.",
      escalation:
        "If the fill is deep, loose or contains obstructions, treat it as a temporary works design job rather than an off-the-shelf box.",
    };
  }

  // ── Firm clay or granular, ≤4 m, dry, no surcharge ────────────────────
  // Still never "safe". If nobody enters, keep people out is the cheapest
  // control. If entry is needed, granular ground gets support; firm shallow
  // clay can be battered to a safe angle by a competent person, but support
  // is always an acceptable (often simpler) alternative.

  if (!entry) {
    return {
      method: "no-entry",
      level: "medium",
      headline: "If nobody needs to enter, keep people out of the excavation",
      reasoning:
        "The simplest control is to avoid entry altogether — work from outside the excavation, and barrier it off so nobody (or any plant) can fall in. You then don't depend on the sides holding.",
    };
  }

  if (soil === "sand-gravel") {
    return {
      method: "support",
      level: "high",
      headline: "Use proprietary support before entry — granular ground won't hold",
      reasoning:
        "Loose sand and gravel run easily and can't be relied on to stand. For entry into granular ground use installed support — a trench box / drag box, or trench sheeting with props — kept ahead of the working face. Battering is possible only at a much flatter angle and a competent person should set it.",
    };
  }

  // firm-clay, shallow or mid, dry, no surcharge, entry needed.
  if (depth === "shallow") {
    return {
      method: "batter",
      level: "medium",
      headline: "Batter the sides to a safe angle, or use a trench box",
      reasoning:
        "In firm, dry clay under 1.2 m with no water or surcharge, a competent person can either batter the sides back to a safe angle of repose or drop in a trench box. Battering needs the room to lay the sides back; where there isn't room, use installed support. Either way, no entry until it's in place.",
    };
  }

  // firm clay, 1.2–4 m, dry, no surcharge, entry needed.
  return {
    method: "support",
    level: "high",
    headline: "Use proprietary support before entry — or batter if there's room",
    reasoning:
      "Between 1.2 m and 4 m, even firm clay should be supported before entry: a trench box / drag box, or sheeting and props, kept ahead of the working face. Battering to a safe angle is an alternative only where there's space to lay the sides back, and a competent person should set the angle.",
    escalation:
      "If conditions turn out worse than expected when you open up (water, soft spots, fill), stop and get a temporary works design.",
  };
}

/* The non-negotiables — shown on every result and on the printout. These are
   the rules that don't change with the inputs (HSE excavations guidance +
   CDM 2015 reg 22). */
const NON_NEGOTIABLES: string[] = [
  "No unsupported entry where there's any collapse risk. HSE is clear that no ground can be relied upon to stand unsupported in all circumstances — and a cubic metre of soil can weigh in excess of 1.5 tonnes, enough to crush or suffocate anyone it falls on.",
  "Decide and provide the support before digging. Work out what's needed (trench box, sheets, props, baulks) and have it on site before work starts — battered or supported before anyone enters, never afterwards.",
  "Daily and post-event inspection by a competent person (CDM 2015 reg 22). No one works in a supported or battered excavation until a competent person has inspected it at the start of each shift, after anything that could affect its stability, and after any fall of material.",
  "Safe access. Provide ladder (or other) access within easy reach of where people are working, so no one is ever far from a way out.",
  "Keep spoil and plant back from the edge. Loadings from spoil heaps, plant and vehicles near the edge make collapse more likely — keep them well back.",
  "Stop people and plant falling in. Barrier the edges and use stop blocks for vehicles tipping or working near the excavation.",
  "Locate buried services first. Get service plans and use a CAT and Genny to find and mark services before breaking ground (HSG47); hand-dig to expose them.",
];

const METHOD_LABEL: Record<Method, string> = {
  engineer: "Temporary works engineer / competent-person design",
  batter: "Batter back to a safe angle (or trench box)",
  support: "Proprietary support — trench box / drag box / sheeting",
  "no-entry": "Keep people out — work from outside the excavation",
};

/* ── Step machine ────────────────────────────────────────────────────────── */

type Step =
  | "depth"
  | "soil"
  | "water"
  | "surcharge"
  | "services"
  | "entry"
  | "duration"
  | "result";

const ORDER: Step[] = ["depth", "soil", "water", "surcharge", "services", "entry", "duration", "result"];

export function ExcavationSupportSelector() {
  const [step, setStep] = useState<Step>("depth");
  const [history, setHistory] = useState<Step[]>([]);
  const [a, setA] = useState<Answers>({});
  const tracked = useRef(false);

  const go = (next: Step) => {
    setHistory((h) => [...h, step]);
    setStep(next);
  };
  const back = () => {
    setHistory((h) => {
      if (h.length === 0) return h;
      setStep(h[h.length - 1]);
      return h.slice(0, -1);
    });
  };
  const restart = () => {
    setA({});
    setHistory([]);
    setStep("depth");
    tracked.current = false;
  };

  const outcome = useMemo(() => (step === "result" ? decide(a) : null), [step, a]);

  useEffect(() => {
    if (outcome && !tracked.current) {
      tracked.current = true;
      trackToolResult("excavation-support-selector", { method: outcome.method });
    }
  }, [outcome]);

  const current = ORDER.indexOf(step) + 1;
  const totalSteps = ORDER.length;

  const buildCopyText = () => {
    if (!outcome) return "";
    const L: string[] = [];
    L.push("EXCAVATION SUPPORT — DECISION RECORD");
    L.push(new Date().toLocaleString("en-GB", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }));
    L.push("");
    L.push(`RECOMMENDATION: ${outcome.headline}`);
    L.push(outcome.reasoning);
    if (outcome.escalation) L.push(`Escalation: ${outcome.escalation}`);
    L.push("");
    L.push("ANSWERS GIVEN");
    if (a.depth) L.push(`- Depth: ${DEPTH_LABEL[a.depth]}`);
    if (a.soil) L.push(`- Ground: ${SOIL_LABEL[a.soil]}`);
    if (a.water) L.push(`- Water present: ${cap(a.water)}`);
    if (a.surcharge) L.push(`- Surcharge nearby (spoil/plant/traffic/structures): ${cap(a.surcharge)}`);
    if (a.services) L.push(`- Buried services located: ${cap(a.services)}`);
    if (a.entry) L.push(`- Anyone entering the excavation: ${cap(a.entry)}`);
    if (a.duration) L.push(`- Duration: ${a.duration === "short" ? "Single shift" : "Left open / re-entered"}`);
    L.push("");
    L.push("NON-NEGOTIABLES (apply regardless)");
    NON_NEGOTIABLES.forEach((n) => L.push(`- ${n}`));
    L.push("");
    L.push("This is a planning aid, not a temporary works design. No excavation is ever \"safe\" — it is made safe by support/battering, competent-person inspection and a safe system of work. Source: HSE excavations guidance and CDM 2015 reg 22.");
    return L.join("\n");
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_400px]">
      {/* ── Wizard ─────────────────────────────────────────────────────── */}
      <div className="card shadow-soft p-6 print-hide">
        <div className="flex items-center justify-between">
          <span className="text-[0.78rem] font-bold uppercase tracking-wide text-muted">
            {step === "result" ? "Result" : `Step ${current} of ${totalSteps}`}
          </span>
          {history.length > 0 && (
            <button
              type="button"
              onClick={back}
              className="inline-flex items-center gap-1 text-[0.82rem] font-semibold text-muted hover:text-ink"
            >
              ← Back
            </button>
          )}
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-mist">
          <div className="h-full rounded-full bg-emerald transition-all" style={{ width: `${(current / totalSteps) * 100}%` }} />
        </div>

        <div className="mt-6">
          {step === "depth" && (
            <Question title="How deep is the excavation?" note="If you genuinely don't know yet, pick the deepest option — it routes you to a designer, which is the safe default.">
              {(Object.keys(DEPTH_LABEL) as Depth[]).map((d) => (
                <ChoiceButton
                  key={d}
                  selected={a.depth === d}
                  onClick={() => {
                    setA((s) => ({ ...s, depth: d }));
                    go("soil");
                  }}
                >
                  {DEPTH_LABEL[d]}
                </ChoiceButton>
              ))}
            </Question>
          )}

          {step === "soil" && (
            <Question title="What's the ground like?" note="Go on what you can actually see and what's known locally — not what you hope it is.">
              {(Object.keys(SOIL_LABEL) as Soil[]).map((s) => (
                <ChoiceButton
                  key={s}
                  selected={a.soil === s}
                  onClick={() => {
                    setA((st) => ({ ...st, soil: s }));
                    go("water");
                  }}
                >
                  {SOIL_LABEL[s]}
                </ChoiceButton>
              ))}
            </Question>
          )}

          {step === "water" && (
            <Question title="Is there water in or running into the excavation?" note="Groundwater, a high water table, rain run-in or a leaking service all count.">
              <YesNoUnsure
                question="Is there water in or running into the excavation?"
                onYes={() => { setA((s) => ({ ...s, water: "yes" })); go("surcharge"); }}
                onNo={() => { setA((s) => ({ ...s, water: "no" })); go("surcharge"); }}
                onUnsure={() => { setA((s) => ({ ...s, water: "unsure" })); go("surcharge"); }}
              />
            </Question>
          )}

          {step === "surcharge" && (
            <Question title="Is there extra load near the edge?" note="Spoil heaps, plant, site traffic, a road, or building/wall foundations within roughly the depth of the dig all add load (surcharge) to the sides.">
              <YesNoUnsure
                question="Is there spoil, plant, traffic or a structure within about the depth of the dig?"
                onYes={() => { setA((s) => ({ ...s, surcharge: "yes" })); go("services"); }}
                onNo={() => { setA((s) => ({ ...s, surcharge: "no" })); go("services"); }}
                onUnsure={() => { setA((s) => ({ ...s, surcharge: "unsure" })); go("services"); }}
              />
            </Question>
          )}

          {step === "services" && (
            <Question title="Have buried services been located and marked?" note="Service plans obtained and a CAT and Genny scan done by a competent operator before breaking ground (HSG47).">
              <YesNoUnsure
                question="Have buried services been located and marked?"
                onYes={() => { setA((s) => ({ ...s, services: "yes" })); go("entry"); }}
                onNo={() => { setA((s) => ({ ...s, services: "no" })); go("entry"); }}
                onUnsure={() => { setA((s) => ({ ...s, services: "unsure" })); go("entry"); }}
              />
            </Question>
          )}

          {step === "entry" && (
            <Question title="Will anyone need to enter the excavation?" note="Including reaching in to lay pipe, connect, inspect or dig by hand.">
              <YesNoUnsure
                question="Will anyone need to enter the excavation?"
                onYes={() => { setA((s) => ({ ...s, entry: "yes" })); go("duration"); }}
                onNo={() => { setA((s) => ({ ...s, entry: "no" })); go("duration"); }}
                onUnsure={() => { setA((s) => ({ ...s, entry: "unsure" })); go("duration"); }}
              />
            </Question>
          )}

          {step === "duration" && (
            <Question title="How long is the excavation open?" note="Ground that stood up at first can deteriorate — rain, vibration and drying all weaken the sides over time.">
              <ChoiceButton
                selected={a.duration === "short"}
                onClick={() => { setA((s) => ({ ...s, duration: "short" })); go("result"); }}
              >
                A single shift, then backfilled
              </ChoiceButton>
              <ChoiceButton
                selected={a.duration === "extended"}
                onClick={() => { setA((s) => ({ ...s, duration: "extended" })); go("result"); }}
              >
                Left open overnight or re-entered over several days
              </ChoiceButton>
            </Question>
          )}

          {step === "result" && outcome && (
            <div>
              <ResultBanner level={outcome.level} title={outcome.headline}>
                <span>{outcome.reasoning}</span>
              </ResultBanner>

              {outcome.escalation && (
                <div className="mt-4 rounded-lg border border-risk-high/40 bg-[#fef0e6] px-4 py-3 text-[0.88rem] leading-relaxed text-[#b34e10]">
                  <strong className="block">Why this isn&apos;t a do-it-yourself answer</strong>
                  {outcome.escalation}
                </div>
              )}

              {a.duration === "extended" && outcome.method !== "engineer" && (
                <p className="mt-4 rounded-lg bg-mist/70 px-3.5 py-2.5 text-[0.85rem] leading-relaxed text-body">
                  You said this is left open or re-entered. Re-inspect before every shift and after rain or
                  any event that could affect stability — ground that held at first can fail later.
                </p>
              )}

              {(a.services === "no" || a.services === "unsure") && (
                <p className="mt-4 rounded-lg bg-mist/70 px-3.5 py-2.5 text-[0.85rem] leading-relaxed text-body">
                  Buried services aren&apos;t confirmed located. Don&apos;t break ground until service plans are
                  obtained and a CAT and Genny scan is done — see the underground-services rule below.
                </p>
              )}

              <div className="mt-5 rounded-xl border border-line bg-mist/50 p-4">
                <div className="text-[0.8rem] font-bold uppercase tracking-wide text-emerald-deep">
                  Non-negotiables — apply whatever the recommendation
                </div>
                <ul className="mt-2.5 space-y-2 text-[0.88rem] leading-relaxed text-body">
                  {NON_NEGOTIABLES.map((n) => (
                    <li key={n} className="flex gap-2.5">
                      <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-deep" />
                      <span>{n}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={back}
                  className="inline-flex items-center gap-1.5 rounded-full border border-line-2 px-4 py-2 text-sm font-semibold text-ink hover:border-ink hover:bg-mist"
                >
                  ← Change last answer
                </button>
                <button
                  type="button"
                  onClick={restart}
                  className="inline-flex items-center gap-1.5 rounded-full border border-line-2 px-4 py-2 text-sm font-semibold text-ink hover:border-ink hover:bg-mist"
                >
                  Start again
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 rounded-xl border border-line bg-mist/60 px-4 py-3 text-sm text-body">
          This is a planning steer, not a temporary works design. Need to record the controls?{" "}
          <Link href="/builder/" className="font-semibold text-emerald-deep underline">
            Build a site-specific RAMS draft
          </Link>
          .
        </div>
      </div>

      {/* ── Printable decision record ─────────────────────────────────── */}
      <div className="space-y-4">
        {outcome ? (
          <>
            <div className="print-area card shadow-soft p-6" id="excavation-record">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                Excavation support — decision record
              </div>
              <h2 className="mt-1 text-lg">
                {new Date().toLocaleString("en-GB", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              </h2>

              <div className="mt-4">
                <ResultBanner level={outcome.level} title={outcome.headline} />
              </div>

              <div className="mt-4">
                <RecordHeading>Recommendation</RecordHeading>
                <p className="mt-1.5 text-sm leading-relaxed text-body">{outcome.reasoning}</p>
                {outcome.escalation && (
                  <p className="mt-2 text-sm leading-relaxed text-[#b34e10]">{outcome.escalation}</p>
                )}
              </div>

              <div className="mt-4">
                <RecordHeading>Answers given</RecordHeading>
                <dl className="mt-1.5 space-y-1.5 text-sm">
                  {a.depth && <RecordLine term="Depth">{DEPTH_LABEL[a.depth]}</RecordLine>}
                  {a.soil && <RecordLine term="Ground observed">{SOIL_LABEL[a.soil]}</RecordLine>}
                  {a.water && <RecordLine term="Water present">{cap(a.water)}</RecordLine>}
                  {a.surcharge && <RecordLine term="Surcharge near edge">{cap(a.surcharge)}</RecordLine>}
                  {a.services && <RecordLine term="Services located">{cap(a.services)}</RecordLine>}
                  {a.entry && <RecordLine term="Anyone entering">{cap(a.entry)}</RecordLine>}
                  {a.duration && (
                    <RecordLine term="Duration">
                      {a.duration === "short" ? "Single shift, then backfilled" : "Left open / re-entered over several days"}
                    </RecordLine>
                  )}
                </dl>
              </div>

              <div className="mt-4">
                <RecordHeading>Non-negotiables</RecordHeading>
                <ul className="mt-1.5 list-disc space-y-1 pl-5 text-[0.84rem] leading-relaxed text-body">
                  {NON_NEGOTIABLES.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              </div>

              <DataReviewedNote date="6 June 2026">
                Guidance follows HSE&apos;s excavations advice and CDM 2015 reg 22. No excavation is ever
                &ldquo;safe&rdquo; in itself — it is made safe by support or battering, competent-person
                inspection and a safe system of work. This is a planning aid, not a temporary works design.
              </DataReviewedNote>

              <PrintFooter toolName="excavation support selector" />
            </div>
            <div className="flex flex-wrap justify-end gap-3">
              <CopyButton tool="excavation-support-selector" buildText={buildCopyText} label="Copy as text" />
              <PrintButton tool="excavation-support-selector" label="Print / save record as PDF" />
            </div>
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Answer the questions to get a conservative support recommendation — battering, a trench box,
            proprietary shoring, or a temporary works engineer — plus the non-negotiables and a printable
            decision record for the H&amp;S file.
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Helpers (local — kept consistent with the RIDDOR checker) ────────────── */

function cap(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function Question({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-xl leading-snug">{title}</h2>
      {note && <p className="mt-2 text-[0.85rem] leading-relaxed text-muted">{note}</p>}
      <div className="mt-4 space-y-3">{children}</div>
    </div>
  );
}

function ChoiceButton({
  children,
  onClick,
  selected,
}: {
  children: React.ReactNode;
  onClick: () => void;
  selected?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left text-[0.92rem] font-semibold leading-snug transition-colors ${
        selected
          ? "border-emerald bg-emerald-tint text-emerald-deep"
          : "border-line-2 bg-white text-ink hover:border-ink hover:bg-mist"
      }`}
    >
      <span>{children}</span>
      <span className="shrink-0 text-muted">→</span>
    </button>
  );
}

function YesNoUnsure({
  question,
  onYes,
  onNo,
  onUnsure,
}: {
  question: string;
  onYes: () => void;
  onNo: () => void;
  onUnsure: () => void;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-3" role="group" aria-label={question}>
      <button type="button" onClick={onYes} className="rounded-xl border border-line-2 bg-white px-4 py-3 text-sm font-semibold text-ink transition-colors hover:border-ink hover:bg-mist">
        Yes
      </button>
      <button type="button" onClick={onNo} className="rounded-xl border border-line-2 bg-white px-4 py-3 text-sm font-semibold text-ink transition-colors hover:border-ink hover:bg-mist">
        No
      </button>
      <button type="button" onClick={onUnsure} className="rounded-xl border border-line-2 bg-white px-4 py-3 text-sm font-semibold text-muted transition-colors hover:border-ink hover:text-ink">
        Not sure
      </button>
    </div>
  );
}

function RecordHeading({ children }: { children: React.ReactNode }) {
  return <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">{children}</div>;
}

function RecordLine({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
      <dt className="shrink-0 font-semibold text-ink sm:w-44">{term}</dt>
      <dd className="text-body">{children}</dd>
    </div>
  );
}
